import { type FC } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'

import { Box, Spinner, colors } from '@factorialco/deprecated-design-system'
import { Datum, ScatterPlot } from '@nivo/scatterplot'

import { fromCents } from 'modules/core/lib/formatCurrencyUtils'

import { useRoleSalaryRanges } from 'modules/job_catalog/hooks/useRoleSalaryRanges'
import { useSalariesDistribution } from 'modules/job_catalog/hooks/useSalariesDistribution'
import { formatSalaryAmount } from 'modules/job_catalog/lib/salaryUtils'

import AreaLayer from './AreaLayer'
import Tooltip from './Tooltip'
import { SalaryDatum, buildSerieData } from './lib'
import theme from './theme'

interface Props {
  roleId: string
}

interface RawSerie<RawDatum extends Datum> {
  id: string | number
  data: RawDatum[]
}

const SalaryScatterPlot: FC<Props> = ({ roleId }) => {
  const [salaryRanges, isSalaryRangesFetching] = useRoleSalaryRanges(roleId)

  const [salariesDistributions, isSalariesDistributionsFetching] = useSalariesDistribution(roleId)
  let max = 0
  let salaryRangesThresholds: Array<{ min: number; max: number; levelName: string }> = []
  const currencies = new Set('')

  const series = salariesDistributions.reduce<RawSerie<SalaryDatum>[]>((acc, x) => {
    if (!x.jobLevel) return acc

    const index = x.jobLevel.order
    const nextData = buildSerieData(x, acc[index]?.data)
    acc[index] = {
      id: x.jobLevel.id,
      data: nextData,
    }

    if (!!x.curatedJobCatalogLevelSalaryMaxRange && x.curatedJobCatalogLevelSalaryMaxRange > max) {
      max = fromCents(x.curatedJobCatalogLevelSalaryMaxRange)
    }

    if (!!x.contractCuratedSalary && x.contractCuratedSalary > max) {
      max = x.contractCuratedSalary
    }

    if (x.legalEntityCurrency) {
      currencies.add(x.legalEntityCurrency)
    }

    const salaryRange = salaryRanges?.find((y) => y.level?.id === x.jobLevel?.id)

    if (salaryRange) {
      salaryRangesThresholds = [
        ...salaryRangesThresholds,
        {
          min: fromCents(salaryRange.minRange),
          max: fromCents(salaryRange.maxRange),
          levelName: x.jobCatalogLevel || '',
        },
      ]
    }

    return acc
  }, [])

  if (isSalaryRangesFetching || isSalariesDistributionsFetching)
    return (
      <Box width="full" height="s400" alignItems="center" justifyContent="center">
        <Spinner />
      </Box>
    )

  // Temporal fix until legal entities selector is implemented
  const formatRightAxis = (value: number) => {
    const currency = Array.from(currencies)[0]
    return currencies.size === 1 ? formatSalaryAmount(value, undefined, currency) : value
  }

  return (
    <Box height="s400" width="full">
      <AutoSizer>
        {({ height, width }: { height: number; width: number }) => (
          <ScatterPlot
            yScale={{ type: 'linear', min: 0, max }}
            xScale={{ type: 'point' }}
            height={height}
            width={width}
            margin={{ right: 60, left: 1, bottom: 24, top: 24 }}
            data={series}
            nodeSize={9}
            axisBottom={null}
            axisRight={{
              tickRotation: 0,
              legendPosition: 'middle',
              legendOffset: 60,
              tickPadding: 10,
              format: (value) => formatRightAxis(value),
            }}
            nodeId={(d) => `${d.serieId}.${d.index}`}
            theme={theme()}
            axisLeft={null}
            axisTop={null}
            layers={[
              'grid',
              'axes',
              (props) => <AreaLayer salaryRangesThresholds={salaryRangesThresholds} {...props} />,
              'nodes',
              'markers',
            ]}
            tooltip={Tooltip}
            colors={colors.purple1000}
          />
        )}
      </AutoSizer>
    </Box>
  )
}

export default SalaryScatterPlot
